/**
 * Secure Storage Adapter for Supabase Auth
 *
 * On native (iOS/Android): uses Keychain / EncryptedSharedPreferences
 * via capacitor-secure-storage-plugin.
 * On web: falls back to localStorage (same behavior as before).
 *
 * Implements Supabase SupportedStorage interface.
 */

import { Capacitor } from '@capacitor/core';

export interface SupportedStorage {
  getItem(key: string): string | null | Promise<string | null>;
  setItem(key: string, value: string): void | Promise<void>;
  removeItem(key: string): void | Promise<void>;
}

/**
 * Creates the appropriate storage adapter for the current platform.
 * Must be called BEFORE createClient().
 *
 * - iOS: Keychain Services (encrypted by Secure Enclave)
 * - Android: EncryptedSharedPreferences (AES-256-GCM, master key in Android Keystore)
 * - Web: localStorage (unchanged behavior)
 */
export async function createSecureStorageAdapter(): Promise<SupportedStorage> {
  if (!Capacitor.isNativePlatform()) {
    return window.localStorage;
  }

  try {
    const { SecureStoragePlugin } = await import('capacitor-secure-storage-plugin');

    // PERF: In-memory cache to avoid redundant native bridge calls.
    // Supabase reads the auth token from storage for EVERY API call's Authorization header.
    // Each native SecureStorage.get() takes ~70ms on Android. At startup, 30+ reads of the
    // same token were adding ~2 seconds of main-thread blocking and causing full context-tree
    // remount cycles (the "double dashboard load" bug). With this cache, only the first read
    // hits the native bridge; subsequent reads are served from RAM in ~0μs.
    // The cache is invalidated on setItem (new value) and removeItem (deletion).
    const cache = new Map<string, string | null>();

    return {
      async getItem(key: string): Promise<string | null> {
        // Return cached value if available (including cached nulls)
        if (cache.has(key)) {
          return cache.get(key)!;
        }

        try {
          const result = await SecureStoragePlugin.get({ key });
          cache.set(key, result.value);
          return result.value;
        } catch {
          // Key not found → cache the null and return (standard behavior)
          cache.set(key, null);
          return null;
        }
      },

      async setItem(key: string, value: string): Promise<void> {
        // Update cache immediately (before the async native call completes)
        cache.set(key, value);
        await SecureStoragePlugin.set({ key, value });
      },

      async removeItem(key: string): Promise<void> {
        // Invalidate cache immediately
        cache.delete(key);
        try {
          await SecureStoragePlugin.remove({ key });
        } catch {
          // Key doesn't exist → ignore silently
        }
      },
    };
  } catch (err) {
    console.warn('[SecureStorage] Plugin not available, falling back to localStorage:', err);
    return window.localStorage;
  }
}

/**
 * One-time migration: moves Supabase auth tokens from localStorage
 * to secure storage on native platforms.
 *
 * Called once at app startup after secure storage is initialized.
 * Idempotent — checks a migration flag to avoid re-running.
 */
export async function migrateFromLocalStorage(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;

  try {
    const { SecureStoragePlugin } = await import('capacitor-secure-storage-plugin');

    // Check if migration already completed
    try {
      const flag = await SecureStoragePlugin.get({ key: '_secure_storage_migrated' });
      if (flag.value === 'true') return;
    } catch {
      // Flag not found → migration not done yet, proceed
    }

    // Find all Supabase auth keys in localStorage
    const supabaseKeys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.startsWith('sb-') || key.startsWith('supabase.'))) {
        supabaseKeys.push(key);
      }
    }

    // Migrate each key to secure storage
    for (const key of supabaseKeys) {
      const value = localStorage.getItem(key);
      if (value) {
        await SecureStoragePlugin.set({ key, value });
        localStorage.removeItem(key);
      }
    }

    // Set migration flag
    await SecureStoragePlugin.set({ key: '_secure_storage_migrated', value: 'true' });

    if (supabaseKeys.length > 0) {
      console.info(`[SecureStorage] Migrated ${supabaseKeys.length} keys from localStorage`);
    }
  } catch (err) {
    console.warn('[SecureStorage] Migration failed (non-fatal):', err);
  }
}
